import { Dayjs } from 'dayjs';
import { format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  ALIMTALK_SEND_HISTORY_AUTO_REFRESH_INTERVAL_MS,
  ALIMTALK_SEND_IN_PROGRESS_STATUSES,
  ALIMTALK_SEND_TYPE_LABEL,
} from './constants';
import {
  AlimtalkSendHistoryListQueryParams,
  AlimtalkSendHistorySearchType,
  AlimtalkSendStatus,
  AlimtalkSendType,
} from './types';

export interface AlimtalkSendHistoryFilter {
  /** 조회 기준 (현재 예약일시 단일) */
  dateBasis: 'scheduledAt';
  dateRange: [Dayjs | null, Dayjs | null] | null;
  status: AlimtalkSendStatus | '';
  sendType: AlimtalkSendType | '';
  searchType: AlimtalkSendHistorySearchType;
  searchValue: string;
}

/**
 *  날짜 경계 기준 시간대 — 백엔드·운영 기준이 KST 라 브라우저 시간대와 무관하게 고정한다.
 *  (레포 관례: dayjs timezone 플러그인 대신 date-fns-tz 사용 — views/promotion/schedule/utils/kst.ts)
 */
const KST_TIME_ZONE = 'Asia/Seoul';

/** 선택한 날짜(달력의 연-월-일)를 KST 하루의 시작/끝 시각으로 해석해 UTC ISO 로 변환한다 */
const toKstDayBoundaryIso = (date: Dayjs, boundary: 'start' | 'end'): string =>
  zonedTimeToUtc(
    `${date.format('YYYY-MM-DD')} ${boundary === 'start' ? '00:00:00.000' : '23:59:59.999'}`,
    KST_TIME_ZONE,
  ).toISOString();

export const DEFAULT_ALIMTALK_SEND_HISTORY_FILTER: AlimtalkSendHistoryFilter = {
  dateBasis: 'scheduledAt',
  dateRange: null,
  status: '',
  sendType: '',
  searchType: 'scheduleId',
  searchValue: '',
};

/** 예약 ID 검색어 형식 — 백엔드(ALIMTALK_SEND006)는 양의 정수(안전 정수 범위)만 허용한다 */
const SCHEDULE_ID_PATTERN = /^\d+$/;

/**
 *  검색유형이 예약 ID 일 때 검색어가 양의 정수가 아닌지 (빈 검색어는 검색어 미사용이라 통과).
 *  검색 박스 입력 검증과 URL 쿼리 복원이 같은 기준을 쓴다.
 */
export const isInvalidScheduleIdSearch = (
  filter: Pick<AlimtalkSendHistoryFilter, 'searchType' | 'searchValue'>,
): boolean => {
  if (filter.searchType !== 'scheduleId') return false;
  const value = filter.searchValue.trim();
  if (!value) return false;
  const numericValue = Number(value);
  // 백엔드와 같은 기준 — 자릿수가 커서 정밀도를 잃는 값(Number.MAX_SAFE_INTEGER 초과)도 거부한다
  return !SCHEDULE_ID_PATTERN.test(value) || !Number.isSafeInteger(numericValue) || numericValue <= 0;
};

/**
 *  필터 폼 + 페이지 상태를 목록 API query params 로 변환한다.
 *  - 날짜는 KST 하루의 시작/끝을 timezone 포함 ISO 문자열로 전송한다.
 *  - 검색어가 비어 있으면 searchType / searchValue 를 생략한다.
 */
export const toListQueryParams = (
  filter: AlimtalkSendHistoryFilter,
  page: number,
  pageSize: number,
): AlimtalkSendHistoryListQueryParams => {
  const [start, end] = filter.dateRange ?? [null, null];
  const searchValue = filter.searchValue.trim();

  const params: AlimtalkSendHistoryListQueryParams = {
    page,
    pageSize,
  };

  if (start) {
    params.startDate = toKstDayBoundaryIso(start, 'start');
  }
  if (end) {
    params.endDate = toKstDayBoundaryIso(end, 'end');
  }
  if (filter.status) {
    params.status = filter.status;
  }
  if (filter.sendType) {
    params.sendType = filter.sendType;
  }
  if (searchValue) {
    params.searchType = filter.searchType;
    params.searchValue = searchValue;
  }

  return params;
};

/** 숫자 표기 — null 은 집계 없음을 뜻하므로 0 으로 단정하지 않고 '-' 로 표기 */
export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : value.toLocaleString();

/**
 *  예약일시 포맷 — 검색 경계와 같은 KST 고정 표기 (브라우저 시간대와 무관).
 *  (레포 관례: views/promotion/schedule/utils/kst.ts 의 utcToZonedTime + date-fns format)
 */
export const formatDateTime = (value: string | null | undefined): string =>
  value ? format(utcToZonedTime(new Date(value), KST_TIME_ZONE), 'yyyy-MM-dd HH:mm:ss') : '-';

/** 발송 유형 라벨 (null 은 유형 미상) */
export const formatSendType = (sendType: AlimtalkSendType | null | undefined): string =>
  sendType ? ALIMTALK_SEND_TYPE_LABEL[sendType] : '-';

/** 발급 쿠폰 표기 — `쿠폰명 (ID)` / 이름 미확인 시 `(ID)` */
export const formatCoupon = (coupon: { id: number; name: string | null }): string =>
  coupon.name ? `${coupon.name} (${coupon.id})` : `(${coupon.id})`;

/** Worker 가 아직 상태를 바꾸는 중(PENDING / RUNNING / CANCELLING)인지 */
export const isInProgressStatus = (status: AlimtalkSendStatus): boolean =>
  ALIMTALK_SEND_IN_PROGRESS_STATUSES.includes(status);

/**
 *  자동 갱신 주기 — 진행 중 상태가 하나라도 있을 때만 주기를 주고, 아니면 끈다(false).
 *  react-query v4 refetchInterval 함수형 옵션에서 쓴다.
 */
export const resolveAutoRefreshInterval = (statuses: readonly AlimtalkSendStatus[] | undefined): number | false =>
  statuses?.some(isInProgressStatus) ? ALIMTALK_SEND_HISTORY_AUTO_REFRESH_INTERVAL_MS : false;

/**
 *  조회 실패를 어떻게 보여줄지.
 *  - 'screen': 화면 전체를 에러 Result 로 바꾼다 — 보여줄 데이터가 없을 때(첫 로드·조건 변경 실패), 또는 권한 없음(403).
 *    403 은 재조회 중에 나도 권한이 회수된 것이라, 더 이상 볼 수 없는 옛 데이터를 남겨 두지 않는다.
 *  - 'banner': 마지막 성공 데이터를 그대로 두고 작은 경고만 띄운다 — 자동 갱신·재조회만 실패한 경우.
 *  - 'none': 실패 아님.
 */
export type QueryFailureView = 'none' | 'banner' | 'screen';

export const resolveQueryFailureView = ({
  isError,
  hasData,
  isForbidden,
}: {
  isError: boolean;
  /** 현재 조회 조건의 마지막 성공 데이터가 있는지 (keepPreviousData 로 남은 이전 조건 데이터는 제외) */
  hasData: boolean;
  isForbidden: boolean;
}): QueryFailureView => {
  if (!isError) return 'none';
  return isForbidden || !hasData ? 'screen' : 'banner';
};

/** 재조회 실패 경고 문구 — 진행 중이라 자동 갱신이 이어질 때만 자동 재시도를 안내한다 */
export const getRefetchFailureMessage = (isAutoRefreshing: boolean): string =>
  isAutoRefreshing
    ? '최신 정보를 불러오지 못했습니다. 잠시 후 자동으로 다시 시도합니다.'
    : '최신 정보를 불러오지 못했습니다. 화면의 정보가 최신이 아닐 수 있습니다.';

/**
 *  성공률(%) 표시값.
 *  - 실패가 1건이라도 있으면 내림 후 99 를 넘지 않게 한다 — 반올림으로 100% 가 보이면 실패가 없는 것처럼 읽힌다
 *    (예: 1,000건 중 3건 실패 → 99.7% → 99%).
 *  - 실패가 0건이면 100 이다 (성공·실패가 모두 0 이어도 0 으로 나누지 않는다 — 화면은 이 경우 집계 없음을 보여준다).
 */
export const calculateSuccessRate = (success: number, failed: number): number => {
  if (failed <= 0) return 100;
  const total = success + failed;
  return Math.min(99, Math.floor((success / total) * 100));
};

/**
 *  화면 표시용 성공률 — 성공·실패 집계가 둘 다 숫자일 때만 계산하고, 하나라도 null(집계 없음)이면 null 을 돌려준다.
 *  null 을 0 으로 바꿔 계산하면 실패 미상인 발송이 100% 로 보이므로 화면은 null 을 '-' 로 표기한다.
 */
export const resolveSuccessRate = (success: number | null, failed: number | null): number | null =>
  success === null || failed === null ? null : calculateSuccessRate(success, failed);
